import { forwardRef, useState, useEffect, useRef, InputHTMLAttributes } from 'react';

interface InputDecimalProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  allowNegative?: boolean;
  label?: string;
  error?: string;
}

export const InputDecimal = forwardRef<HTMLInputElement, InputDecimalProps>(
  ({ value, onChange, decimals = 2, prefix = '', suffix = '', allowNegative = false, label, error, className = '', ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const isFocusedRef = useRef(false);
    const lastValueRef = useRef<number | null | undefined>(undefined);

    const formatNumber = (num: number): string => {
      return num.toLocaleString('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    };

    // Atualiza o display apenas quando o valor externo muda E o input não está focado
    useEffect(() => {
      // Ignora se o input está focado (usuário está digitando)
      if (isFocusedRef.current) return;

      // Ignora se o valor não mudou
      if (value === lastValueRef.current) return;
      lastValueRef.current = value as number | null | undefined;

      if (value === null || value === undefined || value === '') {
        setDisplayValue('');
      } else {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
          setDisplayValue(formatNumber(numValue));
        }
      }
    }, [value, decimals]);

    const parseNumber = (str: string): number | null => {
      if (!str) return null;

      // Remove prefix/suffix and formatting
      let cleaned = str.replace(prefix, '').replace(suffix, '').trim();
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');

      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow empty
      if (!inputValue) {
        setDisplayValue('');
        onChange(null);
        return;
      }

      // Filter allowed characters (digits, comma, dot for thousand separator, minus)
      let filtered = inputValue.replace(/[^\d,.-]/g, '');

      if (!allowNegative) {
        filtered = filtered.replace(/-/g, '');
      }

      // Remove thousand separator dots (user might paste formatted value)
      // But keep the decimal comma
      const parts = filtered.split(',');
      if (parts.length > 1) {
        // Has decimal part
        const intPart = parts[0].replace(/\./g, '');
        const decPart = parts.slice(1).join('').replace(/[.,]/g, '').slice(0, decimals);
        filtered = intPart + ',' + decPart;
      } else {
        // No decimal part yet
        filtered = filtered.replace(/\./g, '');
      }

      setDisplayValue(filtered);

      const numValue = parseNumber(filtered);
      onChange(numValue);
    };

    const handleFocus = () => {
      isFocusedRef.current = true;
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
      const numValue = parseNumber(displayValue);

      if (numValue !== null) {
        setDisplayValue(formatNumber(numValue));
        lastValueRef.current = numValue;
      } else {
        setDisplayValue('');
        lastValueRef.current = null;
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`
              w-full px-3 py-2 border rounded-lg shadow-sm text-right
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              disabled:bg-gray-100 disabled:cursor-not-allowed
              ${prefix ? 'pl-10' : ''}
              ${suffix ? 'pr-10' : ''}
              ${error ? 'border-red-500' : 'border-gray-300'}
              ${className}
            `}
            {...props}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">
              {suffix}
            </span>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

InputDecimal.displayName = 'InputDecimal';
